import React from 'react';
import classes from 'classnames';
import { Link } from 'react-router-dom';
import styles from './PageHeader.module.scss';
import Title from '@/components/school-management/TitleStyles/title';
import { FilterField, FilterValue } from '@/components/school-management/BimloaFilter/BimloaFilter';
import type { CascadingFilterRequest } from '@/school-management/types/response/autocomplete';
import Button from '@/components/school-management/Button/Button';
import { icons } from '@/school-management/utils/icons';
import PageHeaderFiltersBar from './PageHeaderFiltersBar';
import Tooltip, { TooltipPosition } from '@/components/Tooltip/Tooltip';
import { useElementOverflow } from '@/school-management/hooks/useElementOverflow';

interface PageHeaderProps {
  title: string;
  subTitle?: string;
  filterFields?: FilterField[];
  filters?: Record<string, FilterValue>;
  setFilters?: (filters: Record<string, FilterValue>) => void;
  onRemoveFilterLabel?: (key: string) => void;
  linkTo?: string;
  buttonText?: string;
  buttonType?: 'text' | 'icon';
  buttonWidth?: string;
  disabledButton?: boolean;
  disabledFilter?: boolean;
  onIconClick?: () => void;
  onButtonClick?: () => void;
  extraTools?: any;
  className?: string;
  titleClassName?: string;
  subTitleClassName?: string;
  toolsRowClassName?: string;
  anchorFilterOverlay?: boolean;
  anchorFilterOverlayPosition?: 'start' | 'end';
  anchorFilterOverlayVerticalOffset?: number;
  anchorFilterOverlayHorizontalOffset?: number;
  onBack?: () => void;
  organizationId?: string;
  cascadingFilters?: CascadingFilterRequest;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subTitle,
  filterFields,
  filters,
  setFilters,
  onRemoveFilterLabel,
  linkTo,
  buttonText,
  buttonType = 'text',
  buttonWidth,
  disabledButton,
  disabledFilter = false,
  onButtonClick,
  onIconClick,
  extraTools,
  className,
  titleClassName,
  subTitleClassName,
  toolsRowClassName,
  anchorFilterOverlay = true,
  anchorFilterOverlayPosition = 'end',
  anchorFilterOverlayVerticalOffset,
  anchorFilterOverlayHorizontalOffset = 25,
  onBack,
  organizationId,
  cascadingFilters,
}) => {
  const [isTitleOverflow, titleRefCallback] = useElementOverflow<HTMLHeadingElement>([title]);

  const buttonStyle = {
    '--button-width': buttonWidth || '233px',
  } as React.CSSProperties;

  const rowActions =
    extraTools || (buttonType === 'text' && buttonText && (onButtonClick || linkTo));

  return (
    <div
      className={classes(styles.headerRow, {
        [className || '']: !!className,
      })}
    >
      {buttonType === 'icon' ? (
        <div className={styles.titleColumn}>
          <div className={styles.inlineHeader}>
            <div className={styles.inlineTitleRow}>
              <Tooltip
                content={title}
                show={isTitleOverflow}
                position={TooltipPosition.Bottom}
                className={styles.inlineTitleTooltipWrapper}
                tooltipClassName={styles.titleTooltipBox}
                overflowText
              >
                <h1 ref={titleRefCallback} className={styles.inlineMainTitle}>
                  {title}
                </h1>
              </Tooltip>
              {onIconClick ? (
                <button type='button' className={styles.iconLink} onClick={onIconClick}>
                  <img src={icons.pencil} alt='' className={styles.iconImage} />
                </button>
              ) : (
                linkTo && (
                  <Link to={linkTo} className={styles.iconLink}>
                    <img src={icons.pencil} alt='' className={styles.iconImage} />
                  </Link>
                )
              )}
            </div>
            {subTitle && <p className={styles.inlineSubTitle}>{subTitle}</p>}
          </div>
        </div>
      ) : (
        <div className={styles.titleColumn}>
          <Title
            MainTitle={title}
            subTitle={subTitle ? subTitle : ''}
            titleClassName={titleClassName}
            subTitleClassName={subTitleClassName}
            onBack={onBack}
            logo={onBack ? 'ArrowLeft' : undefined}
          />
        </div>
      )}

      <div
        className={classes(styles.toolsRow, {
          [toolsRowClassName || '']: !!toolsRowClassName,
        })}
      >
        {filterFields && filters && setFilters && (
          <PageHeaderFiltersBar
            filterFields={filterFields}
            filters={filters}
            setFilters={setFilters}
            onRemoveFilterLabel={onRemoveFilterLabel}
            organizationId={organizationId}
            cascadingFilters={cascadingFilters}
            disabled={disabledFilter}
            anchorFilterOverlay={anchorFilterOverlay}
            anchorFilterOverlayPosition={anchorFilterOverlayPosition}
            anchorFilterOverlayVerticalOffset={anchorFilterOverlayVerticalOffset}
            anchorFilterOverlayHorizontalOffset={anchorFilterOverlayHorizontalOffset}
          />
        )}

        {rowActions && (
          <div className={styles.toolsRowActions}>
            {buttonType === 'text' && buttonText && onButtonClick && (
              <Button
                className={styles.button}
                style={{ ...buttonStyle }}
                onClick={onButtonClick}
                disabled={disabledButton}
              >
                {buttonText}
              </Button>
            )}
            {buttonType === 'text' && buttonText && !onButtonClick && linkTo && (
              <Link to={linkTo} style={{ textDecoration: 'none' }}>
                <Button className={styles.button} style={{ ...buttonStyle, height: '44px' }}>
                  {buttonText}
                </Button>
              </Link>
            )}
            {extraTools}
          </div>
        )}
      </div>
    </div>
  );
};

export default PageHeader;
